import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, CircularProgress, Portal, Typography } from '@mui/material';
import { PageErrorBoundary } from '../../ErrorBoundary/PageErrorBoundary';
import { ViewName } from '../../../../instrumentation/views';
import { ISetupPdfMessages } from '../../../../types/setupPDFTypes';
import { ISessionUser } from '../../../../types';
import { SelectStoryTypeModal, StoryState } from './SelectStoryType/SelectStoryTypeModal';
import {
  getLocalStorageParsedValue,
  removeLocalStorageItem,
  setOrReplaceLocalStorageItem
} from '../../../utils/localStorageUtils';
import { IAccordionListNode } from '@opengov/component-library/capital/AccordionList/AccordionList';
import { StoriesListSidebar } from './StoriesDrawer/StoriesListSidebar';
import { styles } from './PublicationSetupPDFView.styles';
import { PdfEnhancementPanel } from './PdfEnhancementPanel/PdfEnhancementPanel';
import { PdfStylingTab } from './constants';
import { ITab } from './PdfEnhancementPanel/TabBar/TabBar';
import { fetchStoryPdf, fetchTocPdf, useExportPublicationPdf } from './hooks/usePdfPreviewExport';
import { StoryPreview } from './StoryPreview/StoryPreview';
import {
  DEFAULT_FOOTER_CONTROLS,
  DEFAULT_HEADER_CONTROLS,
  DEFAULT_STORY_MARGINS,
  DEFAULT_TOC_STYLES,
  TOC_NODE_ID
} from '@opengov/stories-shared/constants';
import TOCPreview from './TOCPreview/TOCPreview';
import {
  filterNodesByVisibility,
  generateTocHtml,
  getStoriesIds,
  getStoriesListWithChildrenAtSameLevel,
  getStoriesListWithCoverStory,
  getstoriesVisibilityMap,
  tocNode
} from './SetupPdfUtils';
import Toolbar, { Mode } from '../../SetupPDFComponents/PreviewToolbar/Toolbar';
import {
  ProgressTrackerSettingsProvider
} from '../../StoriesSettings/ProgressTrackerSettings/context/ProgressTracker.Provider';
import { ExportType, SetupPDFHeader } from '../../SetupPDFComponents/SetupPDFHeader/SetupPDFHeader';
import { routerActionCreators } from '@opengov/compose-app-router/state';
import { useToast } from '../../Toast/context/useToast.context';
import { usePublicationSetupPdfStyles } from './hooks/usePublicationSetupPdfStyles';
import { useStoriesHierarchy } from './hooks/useStoriesHierarchy';
import { useUpsertPdfStyles, useDeletePdfStyles, UpsertPdfStylesObject } from './hooks/usePdfStylesMutations';
import { getUserById } from '../../../routes/hasura';
import { PublicationSetupPdfStylesKeyParams } from './hooks/publicationSetupPdfQueryKeys';
import { SetupPdfUiProvider, useSetupPdfUi } from './hooks/useSetupPdfUiContext';

export type IPublicationSetupPdfProps = {
  entityId: number;
  entityName: string;
  entitySubdomain: string;
  messages: ISetupPdfMessages;
  publicationId: string;
  sectionId: string;
  user: ISessionUser;
  navigateToPublicationView(entitySubdomain: string, publicationId: string): void;
};

const SETUP_PDF_STORIES_STORAGE_KEY = 'setupPDF_stories';

const PublicationSetupPdfView = (props: IPublicationSetupPdfProps) => {
  const {
    messages,
    publicationId,
    sectionId,
    entityId,
    user
  } = props;

  // Shared params for all styles hooks (read + mutations). The route always
  // provides exactly one of publicationId / sectionId so this is always set.
  const stylesParams: PublicationSetupPdfStylesKeyParams = publicationId
    ? { exportType: 'publication', exportId: publicationId }
    : { exportType: 'section', exportId: sectionId };

  // Styles read path.
  const pdfStylesQuery = usePublicationSetupPdfStyles(stylesParams);
  const pdfStyles = pdfStylesQuery.data ?? null;
  const isLoadingStyles = pdfStylesQuery.isLoading;
  const isFetchingStyles = pdfStylesQuery.isFetching;

  // Mutation hooks replace the compose upsert/delete effects.
  // `isPending` from upsertMutation replaces compose `isApplyingStyles` state.
  const upsertMutation = useUpsertPdfStyles(stylesParams, messages.somethingWentWrong);
  const deleteMutation = useDeletePdfStyles(stylesParams, messages.somethingWentWrong);
  const exportMutation = useExportPublicationPdf(entityId, messages.somethingWentWrong);
  const isApplyingStyles = upsertMutation.isLoading;
  const [showSelectStoryModal, setShowSelectStoryModal] = useState<boolean>(false);
  const [selectedStoryTypes, setSelectedStoryTypes] = useState<StoryState[] | undefined>(undefined);

  // Stories hierarchy (sections + stories for the sidebar) now from
  // TanStack Query. Replaces `getPublicationWithSectionsAndStories` and
  // `getSectionAndStoriesHeirarchy` compose effects. The query is disabled
  // until `selectedStoryTypes` is set — the user must pick story types from
  // the modal or localStorage before the hierarchy can be fetched.
  // Workaround for a compose-app-router race condition: on page refresh the
  // component mounts before onEnter's fetchRequestedOrCurrentEntity resolves,
  // so the Redux store briefly holds entityId=0. Querying with entityId=0
  // returns an empty array and causes select to throw. This guard goes away
  // with the React Router v7 migration (loaders run before render).
  const isStoreHydrated = entityId > 0;
  const hierarchyParams = selectedStoryTypes && (publicationId || sectionId) && isStoreHydrated
    ? {
        exportType: (publicationId ? 'publication' : 'section') as 'publication' | 'section',
        exportId: publicationId || sectionId,
        selectedStoryTypes,
        entityId
      }
    : null;
  const hierarchyQuery = useStoriesHierarchy(hierarchyParams);
  const storiesSelectData = hierarchyQuery.data?.nodes ?? null;
  const publicationName = hierarchyQuery.data?.publicationName;
  const [pdfEnhancementPanelActiveTab, setPdfEnhancementPanelActiveTab] = useState<ITab>(PdfStylingTab);
  const [openDrawer, setOpenDrawer] = useState(true);
  const iframeDocumentRef = useRef<Document | null>(null);
  const [selectedStoryId, setSelectedStoryId] = useState<string>(TOC_NODE_ID);
  const [activeElement, setActiveElement] = useState<HTMLElement | null>(null);
  const [storiesIds, setStoriesIds] = useState<string[]>([]);
  const [storyMode, setStoryMode] = useState<Mode>('edit');
  const [previewPdfUrl, setPreviewPdfUrl] = useState<string | null>(null);
  const controllerRef = useRef<AbortController | null>(null);
  const [isCreatingPdf, setIsCreatingPdf] = useState<boolean>(false);
  const [zoom, setZoom] = useState<number>(100);
  const containerRef = useRef<HTMLDivElement>(null);
  const storyPreviewRef = useRef<HTMLDivElement>(null);
  const [containerHeight, setContainerHeight] = useState<number>(0);
  const [storyPreviewBaseWidth, setStoryPreviewBaseWidth] = useState<number>(0);
  const { toggleToast } = useToast();
  const { state: uiState, dispatch: uiDispatch } = useSetupPdfUi();
  const { toggledStoriesVisibilityMap, enlargeFonts } = uiState;
  const setToggledStoriesVisibilityMap = (map: { [nodeId: string]: boolean }) =>
    uiDispatch({ type: 'SET_VISIBILITY_MAP', map });
  const [isExportDisabled, setIsExportDisabled] = useState<boolean>(false);
  const [visibleSelectedStories, setVisibleSelectedStories] = useState<IAccordionListNode[]>([]);
  const [isDiscarding, setIsDiscarding] = useState<boolean>(false);
  const isIntialVisibilityMapSet = useRef<boolean>(false);

  // Reset visibility map when navigating between publication and section routes.
  // The component is not unmounted on route change so the ref and map from the
  // previous visit persist — section stories (which share IDs with publication
  // stories) would inherit the publication's visibility state and appear hidden.
  useEffect(() => {
    isIntialVisibilityMapSet.current = false;
    uiDispatch({ type: 'RESET_VISIBILITY_MAP' });
  }, [publicationId, sectionId]);

  // Disable export while stories hierarchy is loading. Without this gate, the
  // export API fires before story/embed content is fully resolved and the backend
  // fetches half-loaded HTML with skeleton loaders instead of actual embeds.
  useEffect(() => {
    setIsExportDisabled(hierarchyQuery.isLoading || isLoadingStyles);
  }, [hierarchyQuery.isLoading, isLoadingStyles]);

  // set initial visibility map based on toc data
  useEffect(() => {
    if (isIntialVisibilityMapSet.current) return;
    if (!storiesSelectData || storiesSelectData.length === 0) return;
    // Wait for the styles query to settle so we can distinguish "no saved styles"
    // from "still loading". Without this guard, a null pdfStyles on first render
    // would leave toggledStoriesVisibilityMap[TOC_NODE_ID] as undefined, making
    // the switch appear OFF while the TOC was actually being shown and exported.
    if (isLoadingStyles || isFetchingStyles) return;

    isIntialVisibilityMapSet.current = true;
    setToggledStoriesVisibilityMap(getstoriesVisibilityMap({
      storiesList: storiesSelectData,
      // Pass '' when no styles saved — getstoriesVisibilityMap defaults all nodes
      // (including TOC) to visible when tocDataString is empty.
      tocDataString: pdfStyles?.tocData ?? '',
      coverStoryId: pdfStyles?.coverStoryId || null
    }));
  }, [storiesSelectData, isLoadingStyles, isFetchingStyles, pdfStyles?.tocData, pdfStyles?.coverStoryId])

  useEffect(() => {
    if (storiesSelectData) {
      const visibleSelectedStories = filterNodesByVisibility(storiesSelectData, toggledStoriesVisibilityMap);
      setVisibleSelectedStories(visibleSelectedStories);
    }
  }, [storiesSelectData, toggledStoriesVisibilityMap]);
  const tocData = useMemo(() => {
    if (!storiesSelectData || storiesSelectData.length === 0) return [];
    const selectedCoverStoryId = pdfStyles?.coverStoryId || null;

    // Get base data structure
    let baseData: IAccordionListNode[];
    if (!selectedCoverStoryId) {
      baseData = getStoriesListWithChildrenAtSameLevel(storiesSelectData || []);
    } else {
      // Remove the first two nodes (cover story and TOC) if cover story is selected
      const updatedData = getStoriesListWithCoverStory(storiesSelectData || [], selectedCoverStoryId);
      baseData = getStoriesListWithChildrenAtSameLevel(updatedData.slice(2));
    }

    // Filter based on toggled stories visibility
    // Only filter if we have visibility map data (stories have been toggled)
    if (Object.keys(toggledStoriesVisibilityMap).length > 0) {
      baseData = filterNodesByVisibility(baseData, toggledStoriesVisibilityMap);
    }

    // Add TOC node at the beginning if visible (default to true if not explicitly set to false)
    const isTocVisible = toggledStoriesVisibilityMap[TOC_NODE_ID] !== false;
    if (isTocVisible) {
      return [tocNode, ...baseData];
    }

    return baseData;
  }, [storiesSelectData, pdfStyles?.coverStoryId, toggledStoriesVisibilityMap]);

  useEffect(() => {
    if (user.id && (publicationId || sectionId)) {
      const storageKey = `${user.id}_${sectionId || publicationId}_${SETUP_PDF_STORIES_STORAGE_KEY}`;
      const storedStories = getLocalStorageParsedValue(storageKey);
      if (storedStories) {
        setSelectedStoryTypes(storedStories as unknown as StoryState[]);
      } else {
        setShowSelectStoryModal(true);
      }
    }
  }, [user.id, publicationId, sectionId]);

  // Handle empty hierarchy result — no stories available for the selected
  // story types. Show a toast, clear the localStorage key and navigate back.
  useEffect(() => {
    if (hierarchyQuery.data && hierarchyQuery.data.nodes.length === 0) {
      toggleToast({ title: messages.noStoriesAvailable, severity: 'error' });
      const storageKey = `${user.id}_${sectionId || publicationId}_${SETUP_PDF_STORIES_STORAGE_KEY}`;
      removeLocalStorageItem(storageKey);
      routerActionCreators.goBack();
    }
  }, [hierarchyQuery.data?.nodes, messages.noStoriesAvailable, publicationId, sectionId, user.id]);

  useEffect(() => {
    const filteredToggledStoriesVisibiltyMap = new Map(Object.entries(toggledStoriesVisibilityMap).filter(([key]) => { return key !== TOC_NODE_ID && key !== pdfStyles?.coverStoryId }))
    const filteredToggledStoriesVisibiltyMapValues = Array.from(filteredToggledStoriesVisibiltyMap.values());
    const allHidden = filteredToggledStoriesVisibiltyMapValues.length == 0 || filteredToggledStoriesVisibiltyMapValues.every(value => value === false);
    if (allHidden) {
      setIsExportDisabled(true);
    } else {
      setIsExportDisabled(false);
    }
  }, [toggledStoriesVisibilityMap, pdfStyles?.coverStoryId]);

  useEffect(() => {
    const container = containerRef.current;
    const storyPreview = storyPreviewRef.current;
    if (!container) return;
    if (!storyPreview) return;

    setContainerHeight(container.clientHeight);
    setStoryPreviewBaseWidth(storyPreview.offsetWidth);

  }, [storyMode, zoom, storiesSelectData]);

  const handlePreviewPdf = async (storyId: string) => {
    if (controllerRef.current) {
      controllerRef.current.abort();
    }
    const controller = new AbortController();
    controllerRef.current = controller;
    setIsCreatingPdf(true);

    // Convert pageMargins from { top, bottom, left, right } (strings) to IStoryMargins format (numbers)
    const storyMargins = { ...DEFAULT_STORY_MARGINS };
    if (pdfStyles?.pageMargins) {
      const { top, bottom, left, right } = pdfStyles.pageMargins;
      storyMargins.marginTop = Number(top);
      storyMargins.marginBottom = Number(bottom);
      storyMargins.marginLeft = Number(left);
      storyMargins.marginRight = Number(right);
    }

    try {
      let pdfUrl: string;
      if (storyId === TOC_NODE_ID) {
        pdfUrl = await fetchTocPdf({
          html: generateTocHtml(tocData, pdfStyles?.tocStyles || DEFAULT_TOC_STYLES, storyMargins),
          publicationId,
          entityId,
          displayTitle: pdfStyles?.footerControls?.showFolderName || false,
          displayDate: pdfStyles?.footerControls?.showTimestamp || false,
          displayPageNumber: pdfStyles?.footerControls?.showPageNumber || false,
          enlargeFonts,
          startingPage: '1',
          storyMarginsEnabled: true,
          margins: storyMargins,
          showHeader: true,
          showFooter: true,
          watermark: pdfStyles?.watermark || null,
          controller
        });
      } else {
        pdfUrl = await fetchStoryPdf({
          displayDate: pdfStyles?.footerControls?.showTimestamp || false,
          displayPageNumber: pdfStyles?.footerControls?.showPageNumber || false,
          displayTitle: pdfStyles?.footerControls?.showFolderName || false,
          enlargeFonts,
          startingPage: '1',
          storyMarginsEnabled: true,
          margins: storyMargins,
          showHeader: true,
          showFooter: true,
          isTemplate: false,
          storyId,
          controller,
          watermark: pdfStyles?.watermark || null,
          isExportFromSetupPdf: true,
          displayDummyPageNumber: pdfStyles?.footerControls?.showPageNumber || false
        });
      }
      if (pdfUrl) {
        setIsCreatingPdf(false);
        setPreviewPdfUrl(pdfUrl);
      }
    } catch (error) {
      if (error instanceof Error && error.name !== 'AbortError') {
        toggleToast({ title: messages.somethingWentWrong, severity: 'error' });
      }
    }
  };

  const handleStorySelection = (storyTypesSelected: StoryState[]) => {
    setSelectedStoryTypes(storyTypesSelected);
    const storageKey = `${user.id}_${sectionId || publicationId}_${SETUP_PDF_STORIES_STORAGE_KEY}`;
    setOrReplaceLocalStorageItem(storageKey, JSON.stringify(storyTypesSelected));
    setShowSelectStoryModal(false);
  };

  const savePdfStyles = async () => {
    const authorId = await getUserById(user.id);
    const object: UpsertPdfStylesObject = {
      createdBy: authorId,
      exportType: stylesParams.exportType,
      exportId: stylesParams.exportId,
      tocData: JSON.stringify(tocData),
      tocStyles: JSON.stringify(pdfStyles?.tocStyles || DEFAULT_TOC_STYLES),
      coverStoryId: pdfStyles?.coverStoryId || null,
      pageMargins: pdfStyles?.pageMargins,
      headerControls: pdfStyles?.headerControls || DEFAULT_HEADER_CONTROLS,
      footerControls: pdfStyles?.footerControls || DEFAULT_FOOTER_CONTROLS,
      watermark: pdfStyles?.watermark || null,
      ...(selectedStoryTypes ? { storyStates: JSON.stringify(selectedStoryTypes) } : {})
    };
    await upsertMutation.mutateAsync({ object });
  };
  const clearSetupPdfStateAndExit = () => {
    routerActionCreators.goBack();
  };

  const handleExport = async () => {
    await savePdfStyles();
    const { showFolderName, showTimestamp, showPageNumber, showFooterOn } =
      pdfStyles?.footerControls || DEFAULT_FOOTER_CONTROLS;
    const { showHeaderOn } = pdfStyles?.headerControls || DEFAULT_HEADER_CONTROLS;

    const storyMargins = { ...DEFAULT_STORY_MARGINS };
    if (pdfStyles?.pageMargins) {
      const { top, bottom, left, right } = pdfStyles.pageMargins;
      storyMargins.marginTop = Number(top);
      storyMargins.marginBottom = Number(bottom);
      storyMargins.marginLeft = Number(left);
      storyMargins.marginRight = Number(right);
    }
    const pdfOptions = {
      display_title: showFolderName,
      display_date: showTimestamp,
      display_page_number: showPageNumber,
      enlarge_fonts: enlargeFonts,
      margins: encodeURIComponent(JSON.stringify(storyMargins)),//Send storyMargins
      show_margins: true,
      show_stories_header: showHeaderOn,
      show_stories_footer: showFooterOn,
      include_story_states: encodeURIComponent(JSON.stringify(selectedStoryTypes)),
      cover_story_id: pdfStyles?.coverStoryId || null,
      is_export_from_setup_pdf: true
    };

    const storageKey = `${user.id}_${sectionId || publicationId}_${SETUP_PDF_STORIES_STORAGE_KEY}`;
    removeLocalStorageItem(storageKey);
    exportMutation.mutate({ publicationId, sectionId, pdfOptions });
    clearSetupPdfStateAndExit();
  };

  const handleSaveAndExit = async () => {
    await savePdfStyles();
    clearSetupPdfStateAndExit();
  };

  const handleDiscard = () => {
    setIsDiscarding(true); // Prevent auto-save from firing
    setShowSelectStoryModal(false);
    const storageKey = `${user.id}_${sectionId || publicationId}_${SETUP_PDF_STORIES_STORAGE_KEY}`;
    removeLocalStorageItem(storageKey);
    void deleteMutation.mutateAsync({ exportId: stylesParams.exportId });
    clearSetupPdfStateAndExit();
  };



  const handleResetControls = () => {
    setZoom(100);
    setPreviewPdfUrl(null);
    setIsCreatingPdf(false);
  };

  const handleStoryChange = useCallback(
    (storyId: string) => {
      setActiveElement(null);
      handleResetControls();
      setSelectedStoryId(storyId);
      if (storyMode === 'preview') {
        void handlePreviewPdf(storyId);
      }
    },
    [storyMode]
  );
  useEffect(() => {
    if (storiesSelectData) {
      const storiesIds = getStoriesIds(storiesSelectData, pdfStyles?.coverStoryId || null, toggledStoriesVisibilityMap);
      if (toggledStoriesVisibilityMap[TOC_NODE_ID] === false && selectedStoryId === TOC_NODE_ID) {
        handleStoryChange(storiesIds[0]);
      }
      setStoriesIds(storiesIds);
    }
  }, [storiesSelectData, pdfStyles?.coverStoryId, toggledStoriesVisibilityMap, selectedStoryId]);

  const handlePreviousOrNextStory = useCallback(
    (direction: 'previous' | 'next') => {
      const currentStoryIndex = storiesIds.indexOf(selectedStoryId);
      if (direction === 'previous' && currentStoryIndex > 0) {
        handleStoryChange(storiesIds[currentStoryIndex - 1]);
      } else if (direction === 'next' && currentStoryIndex < storiesIds.length - 1) {
        handleStoryChange(storiesIds[currentStoryIndex + 1]);
      }
    },
    [storiesIds, selectedStoryId, handleStoryChange]
  );

  return (
    <Box sx={styles.container} key={sectionId ? ExportType.SECTION : ExportType.PUBLICATION}>
      {storiesSelectData && !showSelectStoryModal ? (
        <>
          <SetupPDFHeader
            exportType={sectionId ? ExportType.SECTION : ExportType.PUBLICATION}
            title={messages.setupPdf}
            subtitle={publicationName}
            messages={messages}
            onExport={handleExport}
            onSaveAndExit={handleSaveAndExit}
            onDiscard={handleDiscard}
            isApplyingStyles={isApplyingStyles}
            isExportDisabled={isExportDisabled}
          />
          <Box sx={styles.content}>
            <StoriesListSidebar
              storiesSelectData={storiesSelectData}
              openDrawer={openDrawer}
              setOpenDrawer={setOpenDrawer}
              messages={messages}
              onStoryChange={handleStoryChange}
              coverStoryId={pdfStyles?.coverStoryId || null}
              selectedStoryId={selectedStoryId}
              toggledStoriesVisibilityMap={toggledStoriesVisibilityMap}
              onToggledStoriesVisibilityMapChange={setToggledStoriesVisibilityMap}
              storyMode={storyMode}
            />

            <Box sx={styles.storyContent}>
              <Box sx={styles.toolbarContainer}>
                <Toolbar
                  messages={messages}
                  zoom={zoom}
                  currentStory={storiesIds.indexOf(selectedStoryId) + 1}
                  totalStories={storiesIds.length}
                  selectedStoryId={selectedStoryId}
                  isCreatingPdf={isCreatingPdf}
                  mode={storyMode}
                  onZoomIn={() => {
                    setZoom(zoom + 25);
                  }}
                  onZoomOut={() => {
                    setZoom(zoom - 25);
                  }}
                  onZoomChange={(val) => {
                    setZoom(val);
                  }}
                  onPrevStory={() => handlePreviousOrNextStory('previous')}
                  onNextStory={() => handlePreviousOrNextStory('next')}
                  onModeChange={(mode) => {
                    if (mode === 'preview') {
                      void handlePreviewPdf(selectedStoryId);
                    }
                    setStoryMode(mode);
                  }}
                />
              </Box>
              {storyMode === 'edit' ? (
                <Box ref={containerRef} sx={{ ...styles.storyPreviewContainer }}>
                  <Box sx={{
                    ...styles.storyPreviewWrapper,
                    height: containerHeight > 0 ? `${containerHeight * (zoom / 100)}px` : '100%',
                    width: storyPreviewBaseWidth > 0 ? `${storyPreviewBaseWidth * (zoom / 100)}px` : '100%'
                  }}>
                    <Box ref={storyPreviewRef} sx={{
                      ...styles.storyPreview,
                      height: containerHeight > 0 ? `${containerHeight}px` : '100%',
                      transform: `scale(${zoom / 100})`,
                    }}>
                      {selectedStoryId && selectedStoryId !== TOC_NODE_ID ? (
                        <ProgressTrackerSettingsProvider entityId={entityId}>
                          <StoryPreview
                            activeElement={activeElement}
                            iframeDocumentRef={iframeDocumentRef}
                            messages={messages}
                            publicationCss={hierarchyQuery.data?.publicationCssCode ?? ''}
                            selectedStoryId={selectedStoryId}
                            setActiveElement={setActiveElement}
                          />
                        </ProgressTrackerSettingsProvider>
                      ) : (
                        <TOCPreview
                          data={tocData}
                          onStoryChange={handleStoryChange}
                          messages={messages}
                          tocStyles={pdfStyles?.tocStyles || DEFAULT_TOC_STYLES}
                        />)}
                    </Box>
                  </Box>
                </Box>
              ) : (
                <Box ref={containerRef} sx={{ ...styles.storyPreviewContainer }}>
                  <Box sx={{
                    ...styles.storyPreviewWrapper,
                    height: containerHeight > 0 ? `${containerHeight * (zoom / 100)}px` : '100%',
                    width: storyPreviewBaseWidth > 0 ? `${storyPreviewBaseWidth * (zoom / 100)}px` : '100%'
                  }}>
                    <Box ref={storyPreviewRef} sx={{
                      ...styles.storyPreview,
                      height: containerHeight > 0 ? `${containerHeight}px` : '100%',
                      transform: `scale(${zoom / 100})`,
                    }}>
                      {isCreatingPdf ? (
                        <Box sx={styles.loadingSpinner}>
                          <CircularProgress />
                          <Typography>{messages.generatingPdf}</Typography>
                        </Box>
                      ) : (
                        <iframe
                          src={`${previewPdfUrl}#toolbar=0`}
                          width="100%"
                          height="100%"
                          style={styles.pdfPreview}
                          title={messages.pdfPreview}
                        />
                      )}
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
            {storyMode === 'edit' ? (
              <PdfEnhancementPanel
                activeTab={pdfEnhancementPanelActiveTab}
                navigateToTab={setPdfEnhancementPanelActiveTab}
                messages={messages}
                storiesSelectData={visibleSelectedStories}
                publicationId={publicationId}
                sectionId={sectionId}
                user={user}
                pdfStyles={pdfStyles}
                isLoadingStyles={isLoadingStyles}
                skipAutoSave={isDiscarding}
              />
            ) : null}
          </Box>
        </>
      ) : (
        <Box data-test="setupPDFStoryPreviewLoadingSpinner" sx={styles.loadingSpinner}>
          <CircularProgress />
          <Typography>{messages.loading}</Typography>
        </Box>
      )}
      {showSelectStoryModal && (
        <Portal container={document.getElementById('app')}>
          <SelectStoryTypeModal
            messages={messages}
            onClose={() => {
              setShowSelectStoryModal(false);
              routerActionCreators.goBack();
            }}
            onContinue={handleStorySelection}
          />
        </Portal>
      )}
    </Box>
  );
};

export const PublicationSetupPdf = (props: IPublicationSetupPdfProps) => {
  return (
    <PageErrorBoundary viewName={ViewName.PublicationSetupPdfViewPage}>
      <SetupPdfUiProvider>
        <PublicationSetupPdfView {...props} />
      </SetupPdfUiProvider>
    </PageErrorBoundary>
  );
};
